import { IManualFinderFilterModel, IManualItemModel, IManualModel, } from "../types/manualFinder";
import { IImageMatcherFilterModel } from "../types/imageMatcher";
import { ISpecialToolMatchPayload } from "../services/manualFinder/extractToolAndManualsFromPDF";
import { IVectorizationDataModel, IVectorizedBatchImageModel, } from "../types/vectorization";
import { generateImageOutputURL } from "./storageUtil";

export const specialToolPayloadBuilder = (
  manualFinderFilterProps: IManualFinderFilterModel,
  fileMap: Record<string, string>
) => {
  return {
    topK: manualFinderFilterProps.topKSpecialTools,
    maxNumberOfManuals: manualFinderFilterProps.maxNumberOfManuals,
    image_threshold: manualFinderFilterProps.threshold,
    itemMp: manualFinderFilterProps.selectedItemMP?.value || "",
    fileIds: JSON.stringify(fileMap),
    filter: manualFinderFilterProps.selectedManufacturers.length
      ? {
        $or: manualFinderFilterProps.selectedManufacturers.map(
          (manufacturer) => ({
            Manufacturer: manufacturer.value,
          })
        ),
      }
      : {},
  } as ISpecialToolMatchPayload;
};

export const mapManualItemToManual = (
  manualItem: IManualItemModel
): IManualModel => {
  return {
    manualName: manualItem.manualName,
    manualId: manualItem.manualID,
    itemMpText: manualItem.itemMpText,
    matchedTools: manualItem.matchedSpecialTools
      ? manualItem.matchedSpecialTools.split(", ").map((tool) => tool.trim())
      : [],
    additionalTools: manualItem.additionalSpecialTools
      ? manualItem.additionalSpecialTools.split(", ").map((tool) => tool.trim())
      : [],
    missingTools: manualItem.missingSpecialTools
      ? manualItem.missingSpecialTools.split(", ").map((tool) => tool.trim())
      : [],
  };
};

export const manualModelToCsvBuilder = (data: IManualModel[]) => {
  const headers = [
    "ManualId",
    "ManualName",
    "ItemMpText",
    "MatchedTools",
    "AdditionalTools",
    "MissingTools",
  ];
  const rows = data.map((manual) => [
    manual.manualId,
    manual.manualName.replace(";", ","),
    manual.itemMpText,
    manual.matchedTools.join(", "),
    manual.additionalTools.join(", "),
    manual.missingTools.join(", "),
  ]);
  return [headers.join(";"), ...rows.map((row) => row.join(";"))].join("\n");
};

export const defaultImageMatcherFilter: IImageMatcherFilterModel = {
  selectedItemGroups: [],
  selectedManufacturers: [],
  selectedNameSpace: "All",
  metadata: {},
};

export const buildImageMatcherFilter = (
  imageMatcherFilterProps: IImageMatcherFilterModel = defaultImageMatcherFilter
) => {
  let choice_filter: any = []
  let namespaceFilter =
    ["All", "AimModel", "BtlAll", "BtlSingle"].includes(imageMatcherFilterProps.selectedNameSpace)
      ? { namespace: { $in: ["OEM", "Single"] } }
      : { namespace: imageMatcherFilterProps.selectedNameSpace };

  const hasManufacturers = imageMatcherFilterProps.selectedManufacturers.length > 0;
  const hasItemGroups = imageMatcherFilterProps.selectedItemGroups.length > 0 && !["BtlAll", "BtlSingle"].includes(imageMatcherFilterProps.selectedNameSpace);

  if (hasItemGroups) {
    for (const itemGroup of imageMatcherFilterProps.selectedItemGroups) {
      const subGroupID = itemGroup.value.slice(0, 2);
      const itemGraphicID = itemGroup.value.slice(2);
      if (hasManufacturers) {
        for (const manufacturer of imageMatcherFilterProps.selectedManufacturers) {
          choice_filter.push({
            $and: [
              { SubGroupID: subGroupID },
              { ItemGraphicID: itemGraphicID },
              { Manufacturer: manufacturer.value },
              namespaceFilter,
            ],
          });
        }
      } else {
        choice_filter.push({
          $and: [
            { SubGroupID: subGroupID },
            { ItemGraphicID: itemGraphicID },
            namespaceFilter,
          ],
        });
      }
    }
  } else if (hasManufacturers) {
    for (const manufacturer of imageMatcherFilterProps.selectedManufacturers) {
      choice_filter.push({
        $and: [
          { Manufacturer: manufacturer.value },
          namespaceFilter,
        ],
      });
    }
  } else {
    choice_filter.push({ $and: [namespaceFilter] });
  }

  return {
    $or: choice_filter,
  };
};

export const vectorizedBatchImageBuilder = async (
  image: IVectorizationDataModel
): Promise<IVectorizedBatchImageModel> => {
  return {
    id: image.vectorization_id,
    status: image.status,
    preset: image.parameters.preset,
    url: await generateImageOutputURL(image.vectorization_id, image.status),
    fileName: image.file_name.split(".")[0] + ".svg",
    feedbackText: image.feedback_text,
    feedbackRating: image.feedback_rating,
    svgStrokeCount: image.svg_stroke_count
  };
};
